import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/apiconfig';

const API_URL = `${API_BASE_URL}/api/password-setup`;

export default function SetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [status, setStatus] = useState({ loading: true, error: '', email: '', name: '' });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    async function validate() {
      if (!token) {
        setStatus({ loading: false, error: 'Missing token', email: '', name: '' });
        return;
      }
      try {
        const res = await axios.get(`${API_URL}/validate`, { params: { token } });
        setStatus({ loading: false, error: '', email: res.data.email, name: res.data.name });
      } catch (err) {
        const msg = err.response?.data?.error || err.message;
        setStatus({ loading: false, error: msg, email: '', name: '' });
      }
    }
    validate();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      return setSuccess('');
    }
    if (password !== confirm) {
      return setSuccess('');
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/complete`, { token, password });
      setSuccess('Password set successfully. You can now log in.');
      setTimeout(() => navigate('/'), 1500);
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (status.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-blue-900 to-blue-700">
        <div className="text-gray-600">Validating link...</div>
      </div>
    );
  }

  if (status.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-blue-900 to-blue-700">
        <div className="bg-white p-6 rounded shadow max-w-md w-full text-center">
          <h1 className="text-xl font-semibold mb-2">Invalid or expired link</h1>
          <p className="text-gray-600">{status.error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-blue-900 to-blue-700">
      <div className="bg-white p-6 rounded shadow max-w-md w-full">
        <h1 className="text-2xl font-bold mb-4">Set your password</h1>
        <p className="text-gray-700 mb-6">{status.name ? `Hello ${status.name},` : 'Hello,'} please choose a strong password for {status.email}.</p>
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 p-3 rounded mb-4">{success}</div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm mb-1">New password</label>
            <input
              type="password"
              className="border rounded w-full p-3"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              required
              minLength={8}
            />
          </div>
          <div className="mb-6">
            <label className="block text-sm mb-1">Confirm password</label>
            <input
              type="password"
              className="border rounded w-full p-3"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
            />
            {password && confirm && password !== confirm && (
              <p className="text-red-600 text-sm mt-1">Passwords do not match</p>
            )}
          </div>
          <button
            type="submit"
            disabled={submitting || !password || password !== confirm || password.length < 8}
            className={`w-full py-3 rounded text-white ${submitting || !password || password !== confirm || password.length < 8 ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}
          >
            {submitting ? 'Setting password...' : 'Set password'}
          </button>
        </form>
      </div>
    </div>
  );
}
